import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getPrivileges, getRoleDetails, createRole, updateRole } from '../../utils/mainServices';
import FormHandler from '../../shared/utils/FormHandler';
import { validateRoleForm } from '../../utils/validation';
import FeatherIcon from 'feather-icons-react';
import HierarchicalPrivilegeTable from '../privilagePage/HierarchicalPrivilegeTable';

const asArray = (v) => (Array.isArray(v) ? v : []);

const normalizeTree = (nodes) =>
  asArray(nodes)
    .filter(Boolean)
    .map((n) => {
      const safeActions = asArray(n.actions)
        .filter(Boolean)
        .map((a) => {
          const base = (a && typeof a === 'object') ? a : { action_name: String(a ?? '') };
          return { ...base, selected: false };
        });

      const safeChildren = normalizeTree(asArray(n.child));

      return {
        ...n,
        selected: false,
        actions: safeActions,
        child: safeChildren,
      };
    });

const collectAllActionNames = (nodes, bag = new Set()) => {
  asArray(nodes).forEach((n) => {
    if (!n) return;
    asArray(n.actions).forEach((a) => {
      if (!a) return;
      if (a.action_name != null) bag.add(a.action_name);
    });
    collectAllActionNames(n.child, bag);
  });
  return bag;
};

const flattenTree = (nodes, bag = []) => {
  asArray(nodes).forEach((n) => {
    if (!n) return;
    bag.push(n);
    flattenTree(n.child, bag);
  });
  return bag;
};

const buildIndexById = (nodes) => {
  const idx = new Map();
  flattenTree(nodes).forEach((n) => {
    if (n && n.id != null) idx.set(n.id, n);
  });
  return idx;
};

const collectDescendants = (node, bag = []) => {
  const kids = asArray(node?.child);
  kids.forEach((ch) => {
    if (!ch) return;
    bag.push(ch);
    collectDescendants(ch, bag);
  });
  return bag;
};

const findAncestors = (node, roots) => {
  const idx = buildIndexById(roots);
  const res = [];
  let pid = node?.parentId;
  while (pid && pid !== 'high-privilege') {
    const p = idx.get(pid);
    if (!p) break;
    res.push(p);
    pid = p.parentId;
  }
  return res;
};

const sanitizeActions = (actions) =>
    asArray(actions)
        .filter(Boolean)
        .filter(a => a?.selected === true && a?.action_name)
        .map(a => ({ action_name: String(a.action_name), selected: true }));

const makeCanonical = (n) => ({
    id: n.id,
    name: n.name,
    description: n.description ?? "",
    parentId: n.parentId,
    actions: sanitizeActions(n.actions),
    child: asArray(n.child).filter(Boolean).map(c => ({
        id: c.id,
        name: c.name,
        description: c.description ?? "",
        parentId: c.parentId,
        actions: sanitizeActions(c.actions),
        child: asArray(c.child).filter(Boolean),
    })),
    childIds: [],
});

const indexAllNodes = (list) => {
    const idx = new Map();
    const walk = (nodes) => {
        asArray(nodes).forEach(n => {
            if (!n || !n.id) return;
            if (!idx.has(n.id)) idx.set(n.id, makeCanonical(n));
            const cur = idx.get(n.id);
            const merged = new Map();
            [...cur.actions, ...sanitizeActions(n.actions)].forEach(a => merged.set(a.action_name, a));
            cur.actions = [...merged.values()];
            walk(n.child);
        });
    };
    walk(list);
    return idx;
};

const collectPromotedIds = (idx) => {
    const promoted = new Set();
    idx.forEach((node) => {
        if (asArray(node.actions).length > 0) promoted.add(node.id);
    });
    return promoted;
};

const rebuildChildren = (idx) => {
    idx.forEach((node) => {
        const nextChildren = [];
        asArray(node.child).forEach(c => {
            const cFull = c?.id ? idx.get(c.id) : null;
            if (!cFull) return;
            const childCopy = {
                id: cFull.id,
                name: cFull.name,
                description: cFull.description ?? "",
                parentId: cFull.parentId,
                actions: asArray(cFull.actions),
                child: [],
                childIds: [],
            };
            nextChildren.push(childCopy);
        });
        node.child = nextChildren;
        node.childIds = nextChildren.map(c => c.id);
        node.actions = asArray(node.actions);
    });
    return idx;
};

const buildFinalUserPrivileges = (idx) => {
    const out = [];
    idx.forEach((node) => {
        const cleanNode = {
            id: node.id,
            name: node.name,
            description: node.description ?? "",
            parentId: node.parentId,
            actions: asArray(node.actions),
            child: node.child.map(c => ({
                id: c.id,
                name: c.name,
                description: c.description ?? "",
                parentId: c.parentId,
                actions: asArray(c.actions),
                child: [],
                childIds: [],
            })),
            childIds: node.childIds ?? [],
        };
        out.push(cleanNode);
    });
    const seen = new Set();
    return out.filter(n => (n?.id && !seen.has(n.id)) ? (seen.add(n.id), true) : false);
};

const cleanupPrivilegesForSave = (currentList) => {
    const idx = indexAllNodes(currentList);
    collectPromotedIds(idx);
    rebuildChildren(idx);
    return buildFinalUserPrivileges(idx);
};

const AddNewRole = ({ isVisible, onClose, formType, selectedRole, allRoles }) => {
  const { t } = useTranslation();

  const [hidden, setHidden] = useState(false);
  const [allPrivileges, setAllPrivileges] = useState([]);
  const [allActions, setAllActions] = useState([]);
  const [currentPrivileges, setCurrentPrivileges] = useState([]);
  const [search, setSearch] = useState('');
  const [selectAll, setSelectAll] = useState([]);
  const [expandedParentIds, setExpandedParentIds] = useState([]);

  const [selectedId, setSelectedId] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');

  const [isSelectAllPrivileges, setIsSelectAllPrivileges] = useState(false);
  const [loading, setLoading] = useState(false);

  const isViewMode = formType === 'view';

  const { values, errors, handleSubmit, handleChange, handleOnBlur, initForm } =
    FormHandler(submitRoleForm, (vals) =>
      validateRoleForm(vals, {
        allRoles,
        formType,
        selectedRole,
        privilegesCount: currentPrivileges.length,
      })
    );

  useEffect(() => {
    if (!isVisible) return;

    initForm({
      id: formType === 'edit' || formType === 'view' ? (selectedRole?.id ?? '') : '',
      name: formType === 'edit' || formType === 'view' ? (selectedRole?.name ?? '') : '',
      description: formType === 'edit' || formType === 'view' ? (selectedRole?.description ?? '') : '',
    });

    setSelectedId(formType === 'edit' || formType === 'view' ? (selectedRole?.id?.toString?.() ?? '') : '');
    setSelectedName(formType === 'edit' || formType === 'view' ? (selectedRole?.name ?? '') : '');
    setSelectedDescription(formType === 'edit' || formType === 'view' ? (selectedRole?.description ?? '') : '');
  }, [isVisible, formType, selectedRole]);

  useEffect(() => {
    if (isVisible) getPrivilegesData();
  }, [isVisible]);

  const getPrivilegesData = async () => {
    setLoading(true);
    try {
      const data = await getPrivileges();

      if (data.message === 'Success') {
        const roots = normalizeTree(Array.isArray(data.content) ? data.content : []);
        const actions = Array.from(collectAllActionNames(roots));
        const flat = flattenTree(roots);

        setAllActions(actions);
        setAllPrivileges(roots);
        setSelectAll(flat);

        if (formType === 'edit' || formType === 'view') {
          await getCurrentPrivilege(roots);
        }
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to fetch privileges');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPrivilege = async (newAllPrivilege) => {
    try {
      const data = await getRoleDetails(selectedRole.id);
      if (data.message !== 'Success') return;

      let current = asArray(data.content?.userPrivileges)
        .filter(Boolean)
        .map(p => {
          const np = {
            ...p,
            actions: asArray(p.actions).map(a => ({ ...a })),
            child: asArray(p.child).map(c => ({ ...c })),
          };
          const isPureLeaf = np.child.length === 0 && np.actions.length === 0;
          if (isPureLeaf && (np.selected === undefined || np.selected === null)) {
            np.selected = true;
          }
          return np;
        });

      current.sort((a, b) => {
        if (a.parentId === b.id) return 1;
        if (b.parentId === a.id) return -1;
        return (b.id || 0) - (a.id || 0);
      });

      setCurrentPrivileges(current);
      syncAllPrivilegesWithCurrent(current, newAllPrivilege);
    } catch (error) {
            toast.error('Failed to fetch role details');
    }
  };

  const syncAllPrivilegesWithCurrent = (newCurrentList, baseTree) => {
    try {
      const idToPrivilegeMap = {};
      asArray(newCurrentList).forEach(p => {
        if (p && p.id != null) idToPrivilegeMap[p.id] = p;
      });

      const treeSource = Array.isArray(baseTree) && baseTree.length > 0 ? baseTree : (allPrivileges || []);
      const clonedTree = JSON.parse(JSON.stringify(treeSource));

      const updateNode = (node) => {
        const mapped = idToPrivilegeMap[node.id];

        if (Array.isArray(node.actions)) {
          node.actions = node.actions.map((a) => {
            const isSelected = mapped
              ? !!asArray(mapped.actions).find(x => x.action_name === a.action_name && x.selected === true)
              : false;
            return { ...a, selected: isSelected };
          });
        }

        if (Array.isArray(node.child) && node.child.length > 0) {
          node.child = node.child.map((ch) => updateNode(ch));
        }

        const hasActions = Array.isArray(node.actions) && node.actions.length > 0;
        const hasChildren = Array.isArray(node.child) && node.child.length > 0;

        const allActionsSelected = hasActions ? node.actions.every((a) => a.selected === true) : false;
        const anyActionSelected = hasActions ? node.actions.some((a) => a.selected === true) : false;

        if (hasChildren) {
          const allChildrenSelected = node.child.every((c) => c.selected === true);
          const anyChildSelectedOrPartial = node.child.some((c) => c.selected === true || c.selected === 'partial');

          if (allChildrenSelected && (allActionsSelected || !hasActions)) node.selected = true;
          else if (anyActionSelected || anyChildSelectedOrPartial) node.selected = 'partial';
          else node.selected = false;
        } else {
          if (hasActions) {
            node.selected = allActionsSelected ? true : (anyActionSelected ? 'partial' : false);
          } else {
            node.selected = !!idToPrivilegeMap[node.id];
          }
        }

        return node;
      };

      const updatedRoots = Array.isArray(clonedTree) ? clonedTree.map((p) => updateNode(p)) : [];
      setAllPrivileges(updatedRoots);
    } catch {
    }
  };

  const selectPrivileges = (privilege) => {
    const descendants = collectDescendants(privilege);
    const ancestors = findAncestors(privilege, allPrivileges);

    let oldPrivileges = [...currentPrivileges]; 
    const currentlySelected = privilege.selected;

    if (currentlySelected === true || currentlySelected === 'partial') {
      const allToRemoveIds = new Set([privilege.id, ...descendants.map(d => d.id)]);
      oldPrivileges = oldPrivileges
        .map(p => {
          if (allToRemoveIds.has(p.id)) {
            return {
              ...p,
              selected: false,
              actions: (p.actions || []).map(a => ({ ...a, selected: false })),
            };
          }
          return p;
        })
        .filter(p => !allToRemoveIds.has(p.id));

      setIsSelectAllPrivileges(false);
    } else {
      const toAdd = [privilege, ...descendants].map(p => ({
        ...p,
        selected: true,
        actions: (p.actions || []).map(a => ({ ...a, selected: true }))
      }));

      const idxById = new Map(oldPrivileges.map((p, i) => [p.id, i]));
      toAdd.forEach(n => {
        const idx = idxById.get(n.id);
        if (idx != null) oldPrivileges[idx] = n;
        else oldPrivileges.push(n);
      });
    }

    const idxById = new Map(oldPrivileges.map((p, i) => [p.id, i]));
    ancestors.forEach(parent => {
      let parentCopy = idxById.has(parent.id)
        ? { ...oldPrivileges[idxById.get(parent.id)] }
        : { ...parent };

      parentCopy.child = (parentCopy.child || []).map(ch => {
        const hitIdx = idxById.get(ch.id);
        return hitIdx != null ? oldPrivileges[hitIdx] : { ...ch, selected: false, actions: (ch.actions || []).map(a => ({ ...a, selected: false })) };
      });

      const allChildSelected = parentCopy.child.length > 0 && parentCopy.child.every(c => c.selected === true);
      const anyChildSelectedOrPartial = parentCopy.child.some(c => c.selected === true || c.selected === 'partial');

      const hasActions = Array.isArray(parentCopy.actions) && parentCopy.actions.length > 0;
      const allActionsSelected = hasActions ? parentCopy.actions.every(a => a.selected === true) : true;

      if (allChildSelected && allActionsSelected) parentCopy.selected = true;
      else if (anyChildSelectedOrPartial) parentCopy.selected = 'partial';
      else parentCopy.selected = false;

      if (idxById.has(parentCopy.id)) {
        oldPrivileges[idxById.get(parentCopy.id)] = parentCopy;
      } else if (parentCopy.selected) {
        oldPrivileges.push(parentCopy);
        idxById.set(parentCopy.id, oldPrivileges.length - 1);
      }
    });

    setCurrentPrivileges(oldPrivileges);
    syncAllPrivilegesWithCurrent(oldPrivileges, allPrivileges);
  };

  const selectActionsFrom = (privilege, action) => {
    const ancestors = findAncestors(privilege, allPrivileges);

    let oldCurrentPrivilege = [...currentPrivileges];
    const selIdx = oldCurrentPrivilege.findIndex(p => p.id === privilege.id);
    const working = selIdx >= 0 ? { ...oldCurrentPrivilege[selIdx] } : { ...privilege, actions: (privilege.actions || []).map(a => ({ ...a })) };

    const actIdx = working.actions.findIndex(a => a.action_name === action.action_name);
    if (actIdx >= 0) {
      const prev = working.actions[actIdx].selected;
      working.actions[actIdx] = { ...working.actions[actIdx], selected: !prev };
    } else {
      working.actions.push({ action_name: action.action_name, selected: true });
    }

    const hasActions = working.actions.length > 0;
    const allActSel = hasActions ? working.actions.every(a => a.selected === true) : true;
    const anyActSel = hasActions ? working.actions.some(a => a.selected === true) : false;

    const children = (working.child || []);
    const allChildrenSelected = children.length > 0 && children.every(ch => ch.selected === true);
    const anyChildSelectedOrPartial = children.some(ch => ch.selected === true || ch.selected === 'partial');

    if (children.length === 0) {
      working.selected = hasActions ? (allActSel ? true : (anyActSel ? 'partial' : false)) : false;
    } else {
      if (allChildrenSelected && allActSel) working.selected = true;
      else if (anyActSel || anyChildSelectedOrPartial) working.selected = 'partial';
      else working.selected = false;
    }

    if (selIdx >= 0) {
      if (working.selected === false && (!hasActions || working.actions.every(a => a.selected === false))) {
        oldCurrentPrivilege = oldCurrentPrivilege.filter(p => p.id !== working.id);
      } else {
        oldCurrentPrivilege[selIdx] = working;
      }
    } else {
      if (working.selected !== false) {
        oldCurrentPrivilege.push(working);
      } else if (anyActSel) {
        oldCurrentPrivilege.push(working);
      }
    }

    const idxById = new Map(oldCurrentPrivilege.map((p, i) => [p.id, i]));
    ancestors.forEach(parent => {
      let parentCopy = idxById.has(parent.id)
        ? { ...oldCurrentPrivilege[idxById.get(parent.id)] }
        : { ...parent };

      parentCopy.child = (parentCopy.child || []).map(ch => {
        const hitIdx = idxById.get(ch.id);
        return hitIdx != null ? oldCurrentPrivilege[hitIdx] : { ...ch, selected: false, actions: (ch.actions || []).map(a => ({ ...a, selected: false })) };
      });

      const allChildSelected = parentCopy.child.length > 0 && parentCopy.child.every(c => c.selected === true);
      const anyChildSelectedOrPartial = parentCopy.child.some(c => c.selected === true || c.selected === 'partial');

      const hasParentActions = Array.isArray(parentCopy.actions) && parentCopy.actions.length > 0;
      const allParentActionsSelected = hasParentActions ? parentCopy.actions.every(a => a.selected === true) : true;

      if (allChildSelected && allParentActionsSelected) parentCopy.selected = true;
      else if (anyChildSelectedOrPartial) parentCopy.selected = 'partial';
      else parentCopy.selected = false;

      if (idxById.has(parentCopy.id)) {
        oldCurrentPrivilege[idxById.get(parentCopy.id)] = parentCopy;
      } else if (parentCopy.selected) {
        oldCurrentPrivilege.push(parentCopy);
        idxById.set(parentCopy.id, oldCurrentPrivilege.length - 1);
      }
    });

    setCurrentPrivileges([...oldCurrentPrivilege]);
    syncAllPrivilegesWithCurrent(oldCurrentPrivilege, allPrivileges);
  };

  const selectAllPrivileges = (roots) => {
    if (!isSelectAllPrivileges) {
      setIsSelectAllPrivileges(true);
      const flatAllSelected = flattenTree(roots).map(n => ({
        ...n,
        selected: true,
        actions: (n.actions || []).map(a => ({ ...a, selected: true }))
      }));
      setCurrentPrivileges(flatAllSelected);
      syncAllPrivilegesWithCurrent(flatAllSelected, roots);
    } else {
      setIsSelectAllPrivileges(false);
      setCurrentPrivileges([]);
      syncAllPrivilegesWithCurrent([], roots);
    }
  };

  async function submitRoleForm() {
    const cleanedUserPrivileges = cleanupPrivilegesForSave(currentPrivileges);
    const payload = {
      id:
        formType === 'edit'
          ? (selectedRole?.id ?? parseInt(values.id, 10))
          : parseInt(values.id, 10),
      name: values.name?.trim(),
      description: values.description?.trim(),
      userPrivileges: cleanedUserPrivileges,
    };
    const method = formType === 'new' ? 'POST' : 'PUT';
    await apiRequest(payload, method);
  }

  const apiRequest = async (body, method) => {
    setLoading(true);
    try {
      let data = method === 'POST' ? await createRole(body) : await updateRole(selectedRole?.id, body);

      if (data.message === 'Success') {
        setLoading(false);
        onClose(true);
        toast.success(method === 'POST'
          ? `Successfully Created User Role: ${body.name}`
          : `Successfully Updated User Role: ${body.name}`);
      } else {
        toast.error(data.message);
        setLoading(false);
      }
    } catch (error) {
            toast.error(error.response?.data?.message || `Failed to ${method === 'POST' ? 'create' : 'update'} role`);
      setLoading(false);
    }
  };

  useEffect(() => {
    const term = search?.toString().toLowerCase().trim();
    if (!term) {
      setExpandedParentIds([]);
      return;
    }

    const newExpandedIds = new Set();

    const filterTree = (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes.reduce((accumulator, node) => {
        if (!node) return accumulator;
        const nameMatches = node.name?.toString().toLowerCase().includes(term);
        const filteredChildren = filterTree(node.child || []);

        if (nameMatches) {
          accumulator.push(node);
        } else if (filteredChildren.length > 0) {
          newExpandedIds.add(node.id);
          accumulator.push({ ...node, child: filteredChildren });
        }
        return accumulator;
      }, []);
    };

    filterTree(allPrivileges);
    setExpandedParentIds(Array.from(newExpandedIds));
  }, [search, allPrivileges]);

  const filteredPrivileges = useMemo(() => {
    const term = search?.toString().toLowerCase().trim();
    if (!term) return allPrivileges;

    const filterTree = (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes.reduce((accumulator, node) => {
        if (!node) return accumulator;
        const nameMatches = node.name?.toString().toLowerCase().includes(term);
        const filteredChildren = filterTree(node.child || []);
        if (nameMatches) {
          accumulator.push(node);
        } else if (filteredChildren.length > 0) {
          accumulator.push({ ...node, child: filteredChildren });
        }
        return accumulator;
      }, []);
    };

    return filterTree(allPrivileges);
  }, [search, allPrivileges]);

  if (!isVisible) return null;

  return (
    <div className="sa-popup-bg">
      <div className="sa-popup">
        <form className="sa-modal-box-style" style={{maxWidth: "1000px"}} onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <div className="sa-popup-header sticky-sa-popup-header">
            <span className="sa-model-heading">{formType === 'edit' ? 'Update Role' : (formType === 'view' ? 'View Role' : 'Add New Role')}</span>
            <div className="sa-popup-close-icon" onClick={() => onClose(false)}>
              <FeatherIcon className="sa-modal-close-icon" icon="x" />
            </div>
          </div>

          <div className="sa-popup-content" style={{ display: 'block', width: 'calc(100% - 32px)' }}>
            <div className="row">
              <div className="col-md-4">
                <div className="form-group m-b-16">
                  <label>Enter ID</label>
                  <input
                    type="text"
                    name="id"
                    className={`form-control ${errors.id ? 'warning-input' : ''}`}
                    value={values.id ?? ''}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    disabled={formType === 'edit' || isViewMode}
                    placeholder="Enter Role ID"
                  />
                  {errors.id && <p className="warning-input-msg">{errors.id}</p>}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group m-b-16">
                  <label>Enter Role Name</label>
                  <input
                    type="text"
                    name="name"
                    className={`form-control ${errors.name ? 'warning-input' : ''}`}
                    value={values.name ?? ''}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    disabled={isViewMode}
                    placeholder="Enter Role Name"
                  />
                  {errors.name && <p className="warning-input-msg">{errors.name}</p>}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group m-b-16">
                  <label>Enter Description</label>
                  <input
                    type="text"
                    name="description"
                    className="form-control"
                    value={values.description ?? ''}
                    onChange={handleChange}
                    onBlur={handleOnBlur}
                    disabled={isViewMode}
                    placeholder="Enter Role Description"
                  />
                </div>
              </div>
            </div>

            <div className={`accordion highPrivilegeAccording ${(hidden || errors?.privileges) ? 'expanded' : ''}`}>
              <div
                className="accordion-header"
                style={{ height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => setHidden(!hidden)}
              >
                <div className="row align-items-center" style={{ width: '100%' }}>
                  <div className="col-2 d-flex align-items-center">
                    <p className="text" style={{ marginBottom: 0 }}>Privileges</p>
                  </div>
                  <div className="col-10">
                    <hr style={{ margin: 0 }} />
                  </div>
                </div>
                <div style={{ transition: 'transform 0.2s ease', transform: hidden ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <ExpandMoreIcon />
                </div>
              </div>

              {errors?.privileges && (
                <div className="col-12" style={{ padding: '0 16px 16px 16px' }}>
                  <p style={{ color: 'red', fontSize: '0.875rem' }}>
                    {errors.privileges}
                  </p>
                </div>
              )}

            {hidden && (
            <div className="accordion-body">
              {loading ? (
                <div style={{ textAlign: 'center', padding: 20 }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p style={{ marginTop: 10 }}>Loading privileges...</p>
                </div>
              ) : (
                <>
                  {formType === 'view' ? hidden : (
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search privileges..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        disabled={isViewMode}
                      />
                    </div>
                    <div className="col-md-8 d-flex justify-content-end">
                      <div
                        style={{ border: '1px solid #d9d9d9', color: '#000000BF', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        onClick={() => !isViewMode && selectAllPrivileges(allPrivileges)}
                      >
                        <input
                          type="checkbox"
                          readOnly
                          checked={
                            currentPrivileges.length === selectAll.length &&
                            currentPrivileges.every(p => p.selected === true)
                          }
                          style={{ marginRight: 8 }}
                          disabled={isViewMode}
                        />
                        <span>
                          {currentPrivileges.length === selectAll.length &&
                            currentPrivileges.every(p => p.selected === true)
                            ? 'Unselect All'
                            : 'Select All'}
                        </span>
                      </div>
                    </div>
                  </div>
                  )}

                  <div id="main3" className="sa-table-container ">
                    <table className="table table-borderless sa-table-width hier-priv">
                      <thead>
                        <tr>
                          <th className="sa-table-head-sticky" style={{width: "32%"}}>Privileges</th>
                          <th className="sa-table-head-sticky" style={{width: "64%"}}>Actions</th>
                          <th className="sa-table-head-sticky" style={{width: "4%", textAlign: "left"}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        <HierarchicalPrivilegeTable
                          privileges={filteredPrivileges}
                          onPrivilegeSelect={selectPrivileges}
                          onActionSelect={selectActionsFrom}
                          mode={isViewMode ? 'view' : 'select'}
                          showCheckboxes={true}
                          showExpandCollapse={true}
                          defaultExpandedIds={expandedParentIds}
                        />
                      </tbody>
                    </table>
                  </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="sa-popup-btn">
            {isViewMode ? (
              <button
                type="button"
                className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
                onClick={() => onClose(false)}
              >
                {t('button.CANCEL') || 'CANCEL'}
              </button>
            ) : (
              <>
            <button
              type="button"
              className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
              onClick={() => onClose(false)}
              disabled={loading}
            >
              {t('button.CANCEL') || 'CANCEL'}
            </button>
            <button
              type="button"
              className="sa-popup-secondary-btn-style"
              onClick={handleSubmit}
              disabled={loading}
              style={{ backgroundColor: '#5895E9', color: 'white' }}
            >
              {formType === 'edit' ? 'Update' : 'Add'}
            </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};


const ExpandMoreIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 24 24" width="18">
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" fill="#888" />
  </svg>
);

export default AddNewRole;
